/**
 * WorkspaceAddModal - 워크스페이스 추가 모달
 *
 * 새로운 가계부를 추가하는 모달입니다.
 * "기존 스프레드시트 연결" 또는 "새 스프레드시트 생성" 중 선택할 수 있습니다.
 */

import { useState } from 'react'
import { useWorkspaceStore } from '../workspaceStore'
import { clearClientCache } from '../WorkspaceContext'
import { GoogleSheetsClient } from '@/core/api/GoogleSheetsClient'
import { SpreadsheetInitializer } from '@/core/services/SpreadsheetInitializer'
import type { WorkspaceCreationMode } from '../types'
import { useAuth } from '@/core/auth/useAuth'
import { parseSpreadsheetInput } from '@/shared/utils/spreadsheetUrlParser'
import { env } from '@/core/config/env'

interface WorkspaceAddModalProps {
  /**
   * 모달 닫기 핸들러
   */
  onClose: () => void

  /**
   * 초기 모드 (온보딩에서 사용)
   */
  initialMode?: WorkspaceCreationMode
}

/**
 * WorkspaceAddModal 컴포넌트
 */
export function WorkspaceAddModal({
  onClose,
  initialMode = 'connect',
}: WorkspaceAddModalProps) {
  const { addWorkspace, setActiveWorkspace } = useWorkspaceStore()
  const { user } = useAuth()

  // 폼 상태
  const [name, setName] = useState('')
  const [inputValue, setInputValue] = useState('')              // 사용자가 입력한 원본 값
  const [spreadsheetId, setSpreadsheetId] = useState('')        // 파싱된 유효한 ID
  const [validationMessage, setValidationMessage] = useState('') // 실시간 피드백 메시지
  const [mode, setMode] = useState<WorkspaceCreationMode>(initialMode)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  /**
   * 스프레드시트 입력 핸들러
   *
   * 사용자가 URL 또는 ID를 입력하면 실시간으로 파싱하고
   * 추출된 ID를 상태에 저장합니다.
   * 유효성 검증 결과를 즉시 피드백합니다.
   */
  const handleSpreadsheetInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawInput = e.target.value
    setInputValue(rawInput)

    // 빈 입력 처리
    if (!rawInput.trim()) {
      setSpreadsheetId('')
      setValidationMessage('')
      return
    }

    // URL/ID 파싱 및 검증
    const parsedId = parseSpreadsheetInput(rawInput.trim())

    if (parsedId) {
      setSpreadsheetId(parsedId)
      setValidationMessage('✓ 유효한 스프레드시트입니다')
    } else {
      setSpreadsheetId('')
      setValidationMessage('올바른 URL 또는 ID를 입력해주세요')
    }
  }

  /**
   * 클립보드에서 URL 붙여넣기 핸들러
   *
   * 모바일에서 터치 한 번으로 클립보드 내용을 입력할 수 있도록 합니다.
   */
  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText()
      handleSpreadsheetInputChange({ target: { value: text } } as React.ChangeEvent<HTMLInputElement>)
    } catch (err) {
      console.error('클립보드 읽기 실패:', err)
      // 실패 시 사용자에게 수동 붙여넣기 안내 (에러 상태 설정하지 않음)
    }
  }

  /**
   * 스프레드시트에 필요한 시트가 있는지 확인하고, 없으면 생성
   *
   * "기존 스프레드시트 연결" 시 사용되며, SpreadsheetInitializer를 활용하여
   * 필요한 시트들(Transactions, Categories, PaymentMethods, Users, Budgets)을
   * 자동으로 생성합니다.
   *
   * @param spreadsheetId - 확인할 스프레드시트 ID
   * @throws {Error} 시트 생성 실패 시
   */
  const ensureSheetsExist = async (spreadsheetId: string): Promise<void> => {
    const client = new GoogleSheetsClient(spreadsheetId)
    const initializer = new SpreadsheetInitializer(
      client,
      env.INIT_MODE,  // 환경 변수에서 초기화 모드 가져오기 (dev: test, main: production)
      user  // 현재 로그인한 사용자 전달
    )

    // SpreadsheetInitializer의 addSheetsToExisting 메서드 활용
    const result = await initializer.addSheetsToExisting()

    if (!result.success) {
      throw new Error(`시트 생성 실패: ${result.message}`)
    }
  }

  /**
   * 새 스프레드시트 생성
   *
   * SpreadsheetInitializer를 활용하여 중복 코드를 제거하고,
   * 검증된 로직으로 시트를 생성합니다.
   */
  const createNewSpreadsheet = async (workspaceName: string): Promise<string> => {
    // Static 메서드 호출: 인스턴스 생성 불필요
    // 1. 스프레드시트 생성
    const newSpreadsheetId = await GoogleSheetsClient.createSpreadsheet(`${workspaceName} - ourWallet`)

    // 2. SpreadsheetInitializer를 사용하여 필요한 시트들 생성
    await ensureSheetsExist(newSpreadsheetId)

    return newSpreadsheetId
  }

  /**
   * 폼 제출 처리
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // 유효성 검증
    if (!name.trim()) {
      setError('가계부 이름을 입력해주세요')
      return
    }

    if (mode === 'connect' && !spreadsheetId) {
      setError('올바른 스프레드시트 URL 또는 ID를 입력해주세요')
      return
    }
    // spreadsheetId는 이미 handleSpreadsheetInputChange에서 검증되어 저장됨

    setIsLoading(true)

    try {
      let finalSpreadsheetId: string

      if (mode === 'create') {
        // 새 스프레드시트 생성
        finalSpreadsheetId = await createNewSpreadsheet(name.trim())
      } else {
        // 기존 스프레드시트 연결
        finalSpreadsheetId = spreadsheetId.trim()

        // 🆕 연결한 스프레드시트에 필요한 시트 생성
        // 시트가 이미 있으면 자동으로 백업하고 새로 생성
        await ensureSheetsExist(finalSpreadsheetId)
      }

      // 워크스페이스 추가
      const newWorkspace = addWorkspace({
        name: name.trim(),
        spreadsheetId: finalSpreadsheetId,
      })

      // 캐시 초기화
      clearClientCache()

      // 새 워크스페이스를 활성화
      setActiveWorkspace(newWorkspace.id)

      // 페이지 새로고침
      window.location.reload()
    } catch (err) {
      console.error('워크스페이스 추가 실패:', err)
      setError(
        err instanceof Error
          ? err.message
          : '워크스페이스 추가 중 오류가 발생했습니다'
      )
      setIsLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* 헤더 */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            새 가계부 추가
          </h2>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 폼 */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* 가계부 이름 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              가계부 이름 *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="예: 부부 가계부, 개인 가계부"
              disabled={isLoading}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                       focus:ring-2 focus:ring-primary-500 focus:border-transparent
                       disabled:opacity-50"
            />
          </div>

          {/* 모드 선택 */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              스프레드시트 설정 *
            </label>

            {/* 기존 연결 옵션 */}
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="radio"
                name="mode"
                value="connect"
                checked={mode === 'connect'}
                onChange={() => setMode('connect')}
                disabled={isLoading}
                className="mt-1"
              />
              <div>
                <div className="font-medium text-gray-900 dark:text-gray-100">
                  📊 기존 스프레드시트 연결
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  이미 만들어진 가계부 스프레드시트가 있나요?
                </div>
              </div>
            </label>

            {/* 새로 생성 옵션 */}
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="radio"
                name="mode"
                value="create"
                checked={mode === 'create'}
                onChange={() => setMode('create')}
                disabled={isLoading}
                className="mt-1"
              />
              <div>
                <div className="font-medium text-gray-900 dark:text-gray-100">
                  ✨ 새 스프레드시트 생성
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  처음 시작하시나요? 자동으로 생성해드려요
                </div>
              </div>
            </label>
          </div>

          {/* 스프레드시트 URL/ID 입력 (기존 연결 모드일 때만) */}
          {mode === 'connect' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                스프레드시트 URL 또는 ID *
              </label>

              {/* Google Sheets 열기 버튼 */}
              <a
                href="https://docs.google.com/spreadsheets"
                target="_blank"
                rel="noopener noreferrer"
                className="mb-3 w-full px-3 py-2 text-sm text-primary-600 dark:text-primary-400
                         bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800
                         rounded-md hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors
                         flex items-center justify-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                <span>🔗 Google Sheets 열어서 URL 복사하기</span>
              </a>

              {/* 안내 메시지 */}
              <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  💡 <strong>사용 방법:</strong><br/>
                  <br/>
                  <strong>📱 모바일:</strong><br/>
                  1. 위 버튼 클릭 → Google Sheets 앱/웹 열림<br/>
                  2. 스프레드시트 선택<br/>
                  3. 공유 버튼(📤) → "링크 복사"<br/>
                  4. 이 화면으로 돌아와서 "붙여넣기" 버튼 클릭<br/>
                  <br/>
                  <strong>💻 PC:</strong><br/>
                  1. 위 버튼을 클릭해 Google Sheets로 이동<br/>
                  2. 연결할 스프레드시트를 열기<br/>
                  3. 주소창의 URL 전체를 복사<br/>
                  4. 아래 입력란에 붙여넣기
                </p>
              </div>

              {/* URL 입력 필드 */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={inputValue}
                  onChange={handleSpreadsheetInputChange}
                  inputMode="url"
                  autoComplete="url"
                  placeholder="https://docs.google.com/spreadsheets/d/1BxiMVs0XRA5nFMdKv..."
                  disabled={isLoading}
                  className="flex-1 px-4 py-3 h-12 text-base border border-gray-300 dark:border-gray-600 rounded-md
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                           focus:ring-2 focus:ring-primary-500 focus:border-transparent
                           disabled:opacity-50 font-mono"
                />
                <button
                  type="button"
                  onClick={handlePasteFromClipboard}
                  disabled={isLoading}
                  className="px-4 h-12 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600
                           rounded-md text-sm font-medium transition-colors disabled:opacity-50"
                >
                  붙여넣기
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                전체 URL을 붙여넣으면 자동으로 ID를 추출합니다
              </p>
              {validationMessage && (
                <p className={`mt-1 text-xs ${
                  validationMessage.startsWith('✓')
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-amber-600 dark:text-amber-400'
                }`}>
                  {validationMessage}
                </p>
              )}
            </div>
          )}

          {/* 에러 메시지 */}
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* 버튼 */}
          <div className="flex justify-end space-x-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700
                       rounded-md transition-colors disabled:opacity-50"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700
                       transition-colors disabled:opacity-50 flex items-center space-x-2"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span>{mode === 'create' ? '생성 중...' : '추가 중...'}</span>
                </>
              ) : (
                <span>추가</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
